/**
 * 결제 관리 API 클라이언트
 * - /api/admin/payments 경로 사용
 */

import { apiClient } from './client';
import type {
  PaymentListResponse,
  PaymentFilters,
  CreditPaymentListResponse,
  CreditPaymentFilters,
} from '../../types/payment';

/**
 * 결제 API 서비스
 */
export const paymentsApi = {
  /**
   * 결제 내역 목록 조회
   * - 코인 사용 및 취소 내역 통합 조회
   * 
   * @param page 페이지 번호 (1부터 시작)
   * @param size 페이지 크기
   * @param filters 필터 조건
   * @returns 결제 목록, 페이징, 통계
   */
  getPayments: async (
    page: number = 1,
    size: number = 20,
    filters?: PaymentFilters
  ): Promise<PaymentListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    // 필터 추가
    if (filters) {
      if (filters.searchKeyword) {
        params.append('searchKeyword', filters.searchKeyword);
      }
      if (filters.paymentType) {
        params.append('paymentType', filters.paymentType);
      }
      if (filters.paymentStatus && filters.paymentStatus !== 'ALL') {
        params.append('paymentStatus', filters.paymentStatus);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }
    }

    const response = await apiClient.get(
      `/admin/payments?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 사업자 → 멍투데이 크레딧 결제 (PG 충전) 내역.
   */
  getCreditPayments: async (
    page: number = 1,
    size: number = 20,
    filters?: CreditPaymentFilters
  ): Promise<CreditPaymentListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    if (filters) {
      if (filters.searchKeyword) params.append('searchKeyword', filters.searchKeyword);
      if (filters.companySeq != null) params.append('companySeq', String(filters.companySeq));
      if (filters.paymentStatus && filters.paymentStatus !== 'ALL') {
        params.append('paymentStatus', filters.paymentStatus);
      }
      if (filters.paymentMethod && filters.paymentMethod !== 'ALL') {
        params.append('paymentMethod', filters.paymentMethod);
      }
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    const response = await apiClient.get(
      `/admin/payments/credit?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 사업자 크레딧 결제 취소 (관리자).
   *  7일 이내 + 미사용 결제만 가능 — BE 가 재검증.
   */
  cancelCreditPayment: async (
    purchaseCreditSeq: number,
    reason?: string,
  ): Promise<void> => {
    await apiClient.post(
      `/admin/payments/credit/${purchaseCreditSeq}/cancel`,
      { reason: reason ?? '' },
    );
  },
};

